import React, {ReactNode, useMemo} from "react";
import {Alert, Box, Container, Grid, Paper, Skeleton, Stack, Typography} from "@mui/material";
import {RemoteValue} from "../../util/RemoteValue";
import {Loading} from "../loading-data/LoadingData";
import {HeaderBreadcrumbs} from "./HeaderBreadcrumbs";

export function PageCount({totalCount, filteredCount}: {
    totalCount?: number,
    filteredCount?: number,
}) {
    if (totalCount !== undefined && filteredCount !== undefined && totalCount !== filteredCount) {
        return (
            <Typography variant="caption" color="textSecondary">{filteredCount} of {totalCount}</Typography>
        );
    } else if (totalCount !== undefined) {
        return (
            <Typography variant="caption" color="textSecondary">{totalCount}</Typography>
        );
    } else if (filteredCount !== undefined) {
        return (
            <Typography variant="caption" color="textSecondary">{filteredCount}</Typography>
        );
    } else {
        return null;
    }
}

export function StorePageCount<Entities extends { length: number }, Filtered extends {
    length: number
} = Entities>({totalValue, value}: {
    totalValue?: RemoteValue<Entities>,
    value: RemoteValue<Filtered>,
}) {
    const counts: RemoteValue<{ totalCount: number, filteredCount: number | undefined }> = useMemo(
        () => totalValue
            ? RemoteValue
                .combine({totalValue, value})
                .map(({totalValue, value}) => ({
                    totalCount: totalValue.length,
                    filteredCount: value.length,
                }))
            : value.map(value => ({
                totalCount: value.length,
                filteredCount: undefined,
            })),
        [totalValue, value],
    );

    return (
        <Loading
            loading={
                <Skeleton>
                    <PageCount totalCount={10}/>
                </Skeleton>
            }
            value={counts}
            errorElement={''}
            children={({totalCount, filteredCount}) =>
                <PageCount totalCount={totalCount} filteredCount={filteredCount}/>
            }
        />
    );
}

export function Page({
                         header,
                         counter,
                         description,
                         wide = true,
                         error = null,
                         actions,
                         filter,
                         children
                     }: {
    header: ReactNode,
    counter?: ReactNode,
    description?: ReactNode,
    wide?: boolean,
    error?: string | null | false,
    actions?: ReactNode,
    filter?: ReactNode,
    children: ReactNode;
}) {
    const headerBreadcrumbs = useMemo(() => {
        return typeof header === 'string' ? (
            <HeaderBreadcrumbs breadcrumbs={[{
                type: 'text',
                label: header,
            }]}/>
        ) : (
            header
        );
    }, [header]);

    return (
        <Container maxWidth={wide ? false : "md"}>
            <Box sx={{mb: 2}}>
                <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                    <Grid container size="grow" spacing={2} alignItems="center" direction="row">
                        <Box>{headerBreadcrumbs}</Box>
                        <Box>
                            {counter}
                        </Box>
                    </Grid>
                    {actions && (
                        <Stack direction="row" gap={2} alignItems="center">
                            {actions}
                        </Stack>
                    )}
                </Grid>
                {description && (
                    <Typography variant="body1" component="div">
                        {description}
                    </Typography>
                )}
            </Box>
            {error && (
                <Alert severity="error" sx={{mb: 2}}>
                    {error}
                </Alert>
            )}

            {filter && (
                <Paper
                    elevation={2}
                    sx={{
                        position: 'sticky',
                        top: {xs: 56, sm: 64},
                        zIndex: theme => theme.zIndex.appBar - 1,
                        p: 2,
                        mb: 2,
                    }}
                >
                    {filter}
                </Paper>
            )}

            {children}
        </Container>
    );
}
